import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wallet } from "lucide-react";
import { getRecovTarget, setRecovTarget, RECOV_TARGETS, RISK_WARNING } from "@/lib/recovery";

/* 💡 Smart Stake — depth-aware risk planner. You pick how many back-to-back
   losses to recover from and the recovery target (0/5/10%); it computes the
   exact ladder, auto-sizes the base stake so the WORST CASE (every step loses)
   stays within 10% of your balance, and shows that worst case in dollars
   BEFORE you confirm. */
const MIN = 0.35;
const DEPTHS = [4, 5, 6];
// 📐 exact runtime ladder — after each loss the next stake is
// ceil((hole × (1+target)) / 0.95) so ONE win returns every lost cent + target
export const ladderFor = (stake, depth, target = getRecovTarget()) => {
  const steps = [Math.max(MIN, Math.round(stake * 100) / 100)];
  let hole = steps[0];
  for (let i = 0; i < depth; i++) {
    const s = Math.max(MIN, Math.ceil(((hole * (1 + target)) / 0.95) * 100) / 100);
    steps.push(s);
    hole = Math.round((hole + s) * 100) / 100;
  }
  return { steps, maxKick: steps[steps.length - 1], worst: hole };
};
const fitStake = (budget, depth, target) => {
  // largest base stake whose WORST CASE (whole ladder lost) fits the budget
  if (ladderFor(MIN, depth, target).worst > budget) return MIN;
  let s = MIN;
  for (let i = 0; i < 20000; i++) {
    const nxt = Math.round((s + 0.01) * 100) / 100;
    if (ladderFor(nxt, depth, target).worst > budget) break;
    s = nxt;
  }
  return s;
};

export const recommend = (bal, depth = 4, deepRisk = false, target = getRecovTarget(), riskBudget = null) => {
  const b0 = Math.max(0, Number(bal) || 0);
  const maxBudget = b0 * 0.25; // hard ceiling — never plan more than 25% of the account
  const defBudget = b0 * 0.10; // default worst-case cap: 10% of balance
  const customRisk = Number(riskBudget) > 0;
  const budgetRaw = customRisk ? Number(riskBudget) : defBudget;
  const budget = Math.min(budgetRaw, maxBudget);
  const budgetCapped = customRisk && budgetRaw > maxBudget;
  const deepBudget = maxBudget; // 🔓 deep-risk consent cap (25%)
  const minLad = ladderFor(MIN, depth, target);
  const underfunded = b0 > 0 && minLad.worst > budget;
  const canDeepRisk = underfunded && minLad.worst <= deepBudget;
  let stake = underfunded ? MIN : fitStake(budget, depth, target);
  const rawStake = stake;
  const onePct = Math.max(MIN, Math.floor(b0 * 0.01 * 100) / 100);
  if (!customRisk) stake = Math.min(stake, onePct); // base ≤1% of balance (default plan only — a custom budget IS the user's limit)
  const lad = ladderFor(stake, depth, target);
  const affordableDepth = DEPTHS.slice().reverse().find((d) => ladderFor(MIN, d, target).worst <= budget) || null;
  const worst = lad.worst;
  const maxKick = lad.maxKick;
  const lossLock = Math.max(2, Math.ceil(worst));
  const reqBal10 = Math.ceil(minLad.worst / 0.10);
  const reqBal25 = Math.ceil(minLad.worst / 0.25);
  const tier = b0 < 100 ? "STARTER" : b0 < 500 ? "BUILDER" : b0 < 2000 ? "GRINDER" : "WHALE";
  const applied = !underfunded || (deepRisk && canDeepRisk);
  const maxMult = Math.ceil(maxKick / Math.max(MIN, stake)) + 2;
  const autoLowered = !underfunded && rawStake > stake; // base shrunk below the raw budget fit by the 1% rule
  return { stake, lossLock, depth, target, maxKick, worst, ladder: lad.steps, minKick: minLad.maxKick, minWorst: minLad.worst, underfunded, canDeepRisk, deepRisk: deepRisk && canDeepRisk, applied, affordableDepth, reqBal10, reqBal25, maxMult, recovCap: depth, tier, autoLowered, budget, customRisk, budgetCapped };
};

const tgtLabel = (t) => (t === 0 ? "0% (break even)" : `${Math.round(t * 100)}%`);

const SmartStake = ({ open, onOpenChange, balance, currency = "USD", onApply }) => {
  const [bal, setBal] = useState("");
  const [depth, setDepth] = useState(4);
  const [target, setTarget] = useState(getRecovTarget());
  const [deepRisk, setDeepRisk] = useState(false);
  const [risk, setRisk] = useState(""); // 💸 custom worst-case budget ($) — empty = default 10%
  useEffect(() => {
    if (open) { setBal(balance > 0 ? String(Math.round(balance * 100) / 100) : ""); setDeepRisk(false); setRisk(""); setTarget(getRecovTarget()); }
  }, [open, balance]);
  const r = recommend(bal, depth, deepRisk, target, Number(risk) > 0 ? Number(risk) : null);
  const valid = Number(bal) > 0;
  const tgtPct = Math.round(target * 100);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="smartstake-dialog" className="max-w-md bg-[#0B0E14] border-white/15 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono text-base">
            <Wallet className="w-4 h-4 text-[#F7B731]" /> 💡 Smart Stake — size it to survive
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">Your account balance ({currency})</span>
            <input data-testid="smartstake-balance-input" type="number" min="1" step="1" value={bal}
              onChange={(e) => setBal(e.target.value)} placeholder="e.g. 70"
              className="block w-full mt-1 px-3 h-11 rounded-md bg-[#131722] border border-white/15 focus:border-[#F7B731] focus:outline-none text-base font-mono font-bold text-white" />
            {balance > 0 && <span className="text-[9px] font-mono text-white/40">auto-filled from your current account balance — edit if you plan to play with a different amount</span>}
          </label>
          <div>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">Survive how many losses in a row? (max 6)</span>
            <div className="flex gap-2 mt-1">
              {DEPTHS.map((d) => (
                <button key={d} data-testid={`smartstake-depth-${d}`} onClick={() => setDepth(d)}
                  className={`flex-1 h-10 rounded-lg border font-mono font-black text-sm transition-colors ${depth === d ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
                  {d} losses
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">Recovery target — losses back plus…</span>
            <div className="flex gap-2 mt-1">
              {RECOV_TARGETS.map((t) => (
                <button key={t} data-testid={`smartstake-target-${Math.round(t * 100)}`} onClick={() => { setTarget(t); setRecovTarget(t); }}
                  className={`flex-1 h-10 rounded-lg border font-mono font-black text-xs transition-colors ${target === t ? "border-[#089981] bg-[#089981]/15 text-[#089981]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
                  +{tgtLabel(t)}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[8.5px] font-mono text-white/35">lower target = smaller recovery stakes = gentler ladder. 5% is the default.</p>
          </div>
          {/* 💸 custom risk budget — "I have $11k but only want to risk $300" */}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">Max risk — the most you accept losing if the whole ladder loses ($)</span>
            <input data-testid="smartstake-risk-input" type="number" min="1" step="1" value={risk}
              onChange={(e) => setRisk(e.target.value)}
              placeholder={Number(bal) > 0 ? `default: $${Math.max(1, Math.round(Number(bal) * 0.10))} (10% of balance)` : "e.g. 300"}
              className="block w-full mt-1 px-3 h-11 rounded-md bg-[#131722] border border-white/15 focus:border-[#F7B731] focus:outline-none text-base font-mono font-bold text-white" />
            <span className="text-[9px] font-mono text-white/40">your call — e.g. risk only $300 of an $11k account · leave empty for the default 10% · hard-capped at 25% of the balance</span>
          </label>
          {valid && (
            <div data-testid="smartstake-reco" className="rounded-lg border border-[#F7B731]/40 bg-[#F7B731]/5 p-3 space-y-1.5">
              <p className="text-[10px] font-mono font-black text-[#F7B731] tracking-wide">YOUR SETUP · {r.tier} ACCOUNT · DEPTH {r.depth} · +{tgtPct}%</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-[#131722] border border-white/10 py-2">
                  <p className="text-[8px] font-mono text-white/40 uppercase">Base stake</p>
                  <p data-testid="smartstake-reco-stake" className="text-lg font-black font-mono text-[#089981]">${r.stake.toFixed(2)}</p>
                </div>
                <div className="rounded-md bg-[#131722] border border-white/10 py-2">
                  <p className="text-[8px] font-mono text-white/40 uppercase">Loss lock</p>
                  <p data-testid="smartstake-reco-losslock" className="text-lg font-black font-mono text-[#F23645]">${r.lossLock}</p>
                </div>
                <div className="rounded-md bg-[#131722] border border-white/10 py-2">
                  <p className="text-[8px] font-mono text-white/40 uppercase">Biggest kick</p>
                  <p data-testid="smartstake-reco-cap" className="text-lg font-black font-mono text-[#53b9ff]">${r.maxKick.toFixed(2)}</p>
                </div>
              </div>
              {/* 🔴 worst case in dollars — clear colour, BEFORE confirming */}
              <div data-testid="smartstake-worst-case" className="rounded-md border border-[#F23645]/60 bg-[#F23645]/10 p-2 text-center">
                <p className="text-[8px] font-mono text-white/50 uppercase">Worst case — every step of the ladder loses</p>
                <p className="text-xl font-black font-mono text-[#F23645]">−${r.worst.toFixed(2)}</p>
                <p className="text-[8.5px] font-mono text-white/45">that's {Number(bal) > 0 ? `${Math.min(999, Math.round((r.worst / Number(bal)) * 100))}%` : "—"} of this balance — decide now if it's acceptable</p>
              </div>
              {!r.underfunded && (
                <p data-testid="smartstake-autofit-note" className="text-[9px] font-mono text-[#089981]">
                  ✓ base stake auto-sized so this worst case stays within {r.customRisk ? <>your <b>${Math.round(r.budget)}</b> risk budget</> : "10% of your balance"}{r.autoLowered ? " (lowered further by the 1%-of-balance base rule)" : ""}
                </p>
              )}
              {r.budgetCapped && (
                <p data-testid="smartstake-budget-capped" className="text-[9px] font-mono text-[#F7B731]">⚠ your risk budget was capped at 25% of the balance (${Math.round(r.budget)}) — the planner never risks more than that</p>
              )}
              <p className="text-[9px] font-mono text-white/55 leading-snug">
                the plan: lose up to {r.depth} in a row and the recovery kick (max <b className="text-[#53b9ff]">${r.maxKick.toFixed(2)}</b>) wins it all back{tgtPct > 0 ? ` +${tgtPct}%` : ""} — IF it wins. It is never guaranteed.
              </p>
              <div data-testid="smartstake-ladder" className="rounded-md bg-[#131722] border border-white/10 p-2">
                <p className="text-[8px] font-mono text-white/40 uppercase mb-1">Your exact ladder — each stake targets everything back{tgtPct > 0 ? ` +${tgtPct}%` : ""}</p>
                <div className="flex flex-wrap gap-1">
                  {r.ladder.map((s, i) => (
                    <span key={i} data-testid={`smartstake-ladder-step-${i + 1}`}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${i === 0 ? "bg-[#089981]/15 text-[#089981]" : i === r.ladder.length - 1 ? "bg-[#F23645]/15 text-[#ff8a95]" : "bg-white/5 text-white/60"}`}>
                      {i === 0 ? "base" : `L${i}`} ${s.toFixed(2)}
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-[8px] font-mono text-white/35">sums to ${r.worst.toFixed(2)} if ALL {r.ladder.length} lose</p>
              </div>
              {r.underfunded && r.customRisk && (
                <div data-testid="smartstake-budget-low" className="rounded-md border border-[#F23645]/50 bg-[#F23645]/10 p-2 space-y-1.5">
                  <p className="text-[9px] font-mono font-black text-[#F23645]">⚠️ Even at the $0.35 minimum stake, the depth-{r.depth} worst case (${r.minWorst.toFixed(2)}) is more than your ${Math.round(r.budget)} risk budget. Raise the budget to at least ${Math.ceil(r.minWorst)} or pick a lower depth.</p>
                  {r.affordableDepth && r.affordableDepth !== r.depth && (
                    <button data-testid="smartstake-budget-use-affordable" onClick={() => setDepth(r.affordableDepth)}
                      className="w-full h-8 rounded-md bg-[#F23645] text-white text-[10px] font-black font-mono">USE DEPTH {r.affordableDepth} INSTEAD (fits your budget)</button>
                  )}
                </div>
              )}
              {r.underfunded && !r.customRisk && (
                <div data-testid="smartstake-underfunded" className="rounded-md border border-[#F23645]/50 bg-[#F23645]/10 p-2 space-y-1.5">
                  <p className="text-[9px] font-mono font-black text-[#F23645]">⚠️ Even at the $0.35 minimum stake, the depth-{r.depth} worst case (${r.minWorst.toFixed(2)}) is more than 10% of this balance — the base stake can't be lowered any further. It needs a ≥ ${r.reqBal10} balance normally.</p>
                  {r.canDeepRisk ? (
                    <label data-testid="smartstake-deeprisk" className="flex items-start gap-2 cursor-pointer rounded-md border border-[#F7B731]/50 bg-[#F7B731]/10 p-2">
                      <input data-testid="smartstake-deeprisk-check" type="checkbox" checked={deepRisk} onChange={(e) => setDeepRisk(e.target.checked)} className="mt-0.5 accent-[#F7B731]" />
                      <span className="text-[9px] font-mono text-white/75">
                        <b className="text-[#F7B731]">🔓 DEEP RISK MODE</b> — I accept that the worst case (${r.minWorst.toFixed(2)}) can be up to <b>25% of my balance</b>, and it is money I'm ready to lose.
                      </span>
                    </label>
                  ) : (
                    <p className="text-[9px] font-mono text-white/70">
                      This balance can't carry the depth-{r.depth} ladder at all — the worst case would top 25% of the account. Depth {r.depth} unlocks at <b className="text-[#F7B731]">≥ ${r.reqBal25}</b> (deep-risk) or <b>≥ ${r.reqBal10}</b> (standard).
                    </p>
                  )}
                  {r.affordableDepth && r.affordableDepth !== r.depth && !deepRisk && (
                    <button data-testid="smartstake-use-affordable" onClick={() => setDepth(r.affordableDepth)}
                      className="w-full h-8 rounded-md bg-[#F23645] text-white text-[10px] font-black font-mono">USE DEPTH {r.affordableDepth} INSTEAD (fits the 10% worst-case cap)</button>
                  )}
                </div>
              )}
              <p data-testid="smartstake-risk-warning" className="rounded-md border border-[#F23645]/40 bg-[#F23645]/5 p-2 text-[9px] font-mono text-[#ff8a95] leading-snug">
                ⚠️ {RISK_WARNING}
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="smartstake-apply" disabled={!valid || !r.applied}
              onClick={() => { onApply(r); onOpenChange(false); }}
              className="flex-1 h-11 rounded-lg bg-[#F7B731] hover:bg-[#ffc94d] disabled:opacity-40 text-black font-black font-mono text-[12px] tracking-wide transition">
              ✓ APPLY THIS SETUP
            </button>
            <button data-testid="smartstake-skip" onClick={() => onOpenChange(false)}
              className="px-4 h-11 rounded-lg border border-white/20 text-white/60 hover:border-white/40 font-mono text-[11px] font-bold transition">
              I'll set my own
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartStake;
